import React, { useState, useEffect } from "react";
import {
  Table,
  Tag,
  Input,
  Button,
  DatePicker,
  Select,
  Card,
  Tooltip,
  Space,
  Badge,
  Spin,
  Modal,
  message,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/es_ES";
import icono_excel from "../../assets/img/icono_excel.png";
import FormNovedad from "./FormNovedad";
import { novedadesApi, tiposNovedadesApi } from "../../services/api";
import { toast } from "react-toastify";
import moment from "moment";
import DeleteConfirmModal from "../DeleteConfirmModal";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { confirm } = Modal;

const TablaNovedades = () => {
  const [formVisible, setFormVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [campus, setCampus] = useState(null);
  const [tipo, setTipo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novedades, setNovedades] = useState([]);
  const [tiposNovedades, setTiposNovedades] = useState([]);
  const [campusOptions, setCampusOptions] = useState([]);
  const [editingNovedad, setEditingNovedad] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [novedadToDelete, setNovedadToDelete] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    Promise.all([fetchNovedades(), fetchTiposNovedades()])
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);
  // Función para obtener novedades
  const fetchNovedades = async () => {
    try {
      const response = await novedadesApi.getAll();
      if (response.data.status) {
        // Procesar los datos de novedades
        const novedadesArray = Array.isArray(response.data.novedades) 
          ? response.data.novedades 
          : response.data.novedad ? [response.data.novedad] : [];
        
        const novedadesData = novedadesArray.map((novedad) => {
          // Extraer el campus directamente del objeto empleado, que ahora incluye campus anidado
          const campusValue = novedad.empleado?.campus?.campus || "N/A";

          // Añadir campus a las opciones si no existe
          if (campusValue !== "N/A" && !campusOptions.includes(campusValue)) {
            setCampusOptions((prev) => [...prev, campusValue]);
          }

          return {
            key: novedad.id.toString(),
            id: novedad.id,
            fecha: moment(novedad.fecha).format("DD/MM/YYYY"),
            campus: novedad.empleado?.campus?.campus || "N/A",
            empleado: novedad.empleado?.nombre || "N/A",
            cedula: novedad.empleado?.cedula || "N/A",
            cargo: novedad.empleado?.cargo || "N/A",
            dependencia: novedad.empleado?.dependencia?.dependencia || "N/A",
            tipo_novedad: novedad.tipo_novedad?.novedad || "N/A",
            mins: novedad.minutos === 0 ? "N/A" : novedad.minutos.toString(),
            hrs: novedad.horas === 0 ? "N/A" : novedad.horas.toString(),
            dias: novedad.dias === 0 ? "N/A" : novedad.dias.toString(),
            nomina: novedad.nomina,
            observaciones: novedad.observaciones || "Sin observaciones",
            soporte: novedad.soporte,
            fecha_ini: novedad.fecha_ini
              ? moment(novedad.fecha_ini).format("DD/MM/YYYY")
              : "N/A",
            fecha_fin: novedad.fecha_fin
              ? moment(novedad.fecha_fin).format("DD/MM/YYYY")
              : "N/A",
            usuario: novedad.usuario?.name || "N/A",
            tipo_contrato: novedad.empleado?.tipo_contrato?.tipo_contrato || "N/A",
          };
        });
        setNovedades(novedadesData);
      }
    } catch (error) {
      console.error("Error obteniendo novedades:", error);
      toast.error("Error al cargar las novedades", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Función para obtener tipos de novedades
  const fetchTiposNovedades = async () => {
    try {
      const response = await tiposNovedadesApi.getAll();
      if (response.data.status) {
        setTiposNovedades(response.data.tipos_novedades || []);
      }
    } catch (error) {
      console.error("Error obteniendo tipos de novedades:", error);
      toast.error("Error al cargar los tipos de novedades", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };


  const columns = [
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Campus</span>
        </div>
      ),
      dataIndex: "campus",
      key: "campus",
      width: 90,
      filters: campusOptions.map((c) => ({ text: c, value: c })),
      onFilter: (value, record) => record.campus === value,
      align: "center",
      responsive: ["xs", "sm", "md", "lg"],
      render: (text) => (
        <Badge status={text === "BGA" ? "processing" : "warning"} text={text} />
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <CalendarOutlined className="mr-1" />
          <span className="font-medium">Fecha</span>
        </div>
      ),
      dataIndex: "fecha",
      key: "fecha",
      width: 110,
      sorter: (a, b) => {
        const dateA = moment(a.fecha, "DD/MM/YYYY");
        const dateB = moment(b.fecha, "DD/MM/YYYY");
        return dateA - dateB;
      },
      sortDirections: ["descend", "ascend"],
      responsive: ["sm", "md", "lg"],
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Nombre Empleado</span>
        </div>
      ),
      dataIndex: "empleado",
      key: "empleado",
      ellipsis: true,
      width: 150,
      responsive: ["sm", "md", "lg"],
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Cédula</span>
        </div>
      ),
      dataIndex: "cedula",
      key: "cedula",
      width: 120,
      align: "right",
      responsive: ["md", "lg"],
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Cargo</span>
        </div>
      ),
      dataIndex: "cargo",
      key: "cargo",
      ellipsis: true,
      width: 170,
      responsive: ["md", "lg"],
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Dependencia</span>
        </div>
      ),
      dataIndex: "dependencia",
      key: "dependencia",
      ellipsis: true,
      width: 180,
      responsive: ["lg"],
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Tipo Novedad</span>
        </div>
      ),
      dataIndex: "tipo_novedad",
      key: "tipo_novedad",
      width: 170,
      filters: tiposNovedades.map((t) => ({
        text: t?.novedad || "",
        value: t?.novedad || "",
      })),
      onFilter: (value, record) => record.tipo_novedad === value,
      responsive: ["md", "lg"],
      render: (tipo) => {
        return (
          <Tag color="blue" className="text-center py-1 px-2 font-medium">
            {tipo}
          </Tag>
        );
      },
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Mins</span>
        </div>
      ),
      dataIndex: "mins",
      key: "mins",
      width: 80,
      align: "center",
      responsive: ["lg"],
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Hrs</span>
        </div>
      ),
      dataIndex: "hrs",
      key: "hrs",
      width: 80,
      align: "center",
      responsive: ["lg"],
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Días</span>
        </div>
      ),
      dataIndex: "dias",
      key: "dias",
      width: 80,
      align: "center",
      responsive: ["lg"],
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Nómina</span>
        </div>
      ),
      dataIndex: "nomina",
      key: "nomina",
      width: 100,
      align: "center",
      filters: [
        { text: "Sí", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.nomina === value,
      responsive: ["lg"],
      render: (afecta) => (
        <Tag color={afecta ? "success" : "default"} className="mx-auto">
          {afecta ? "SI" : "NO"}
        </Tag>
      ),
    },
    {
      title: (
        <div className="flex items-center">
          <span className="font-medium">Observaciones</span>
        </div>
      ),
      dataIndex: "observaciones",
      key: "observaciones",
      ellipsis: true,
      responsive: ["lg"],
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <div className="truncate max-w-xs cursor-help">{text}</div>
        </Tooltip>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Soporte</span>
        </div>
      ),
      key: "soporte",
      width: 100,
      align: "center",
      responsive: ["lg"],
      render: (_, record) => (
        <Space size="middle">
          {record.soporte ? (
            <Tooltip title="Ver soporte">
              <Button
                type="primary"
                size="small"
                icon={<FilePdfOutlined />}
                className="bg-blue-500 hover:bg-blue-700"
                onClick={() => handleViewSupport(record.id)}
              />
            </Tooltip>
          ) : (
            <Tag color="default">No disponible</Tag>
          )}
        </Space>
      ),
    },
    {
      title: (
        <div className="flex items-center justify-center">
          <span className="font-medium">Acciones</span>
        </div>
      ),
      key: "action",
      width: 120,
      align: "center",
      responsive: ["lg"],
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Editar">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              className="bg-yellow-500 hover:bg-yellow-600 border-yellow-500"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              type="primary"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleReset = () => {
    setSearchText("");
    setDateRange([null, null]);
    setCampus(null);
    setTipo(null);
    fetchNovedades(); // Recargar los datos
  };
  const onAddNovedad = () => {
    setEditingNovedad(null);
    setFormVisible(true);
  };

  const handleEdit = (record) => {
    setEditingNovedad(record);
    setFormVisible(true);
  };
  const handleDelete = (record) => {
    setNovedadToDelete(record);
    setDeleteModalVisible(true);
  };  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await novedadesApi.delete(novedadToDelete.id);
      
      if (response.data.status) {
        toast.success(`Novedad de ${novedadToDelete.empleado} del ${novedadToDelete.fecha} eliminada correctamente`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setDeleteModalVisible(false);
        setNovedadToDelete(null);
        fetchNovedades(); // Recargar las novedades
      } else {
        toast.error('Error al eliminar la novedad');
      }
    } catch (error) {
      console.error('Error eliminando novedad:', error);
      let errorMsg = 'Error al eliminar la novedad';
      if (error.response && error.response.data && error.response.data.message) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Función para visualizar el soporte de una novedad
  const handleViewSupport = async (id) => {
    try {
      const response = await novedadesApi.getFile(id);
      
      // Crear una URL para el blob
      const url = window.URL.createObjectURL(response.data);
      
      // Abrir en una nueva pestaña
      window.open(url, '_blank');
    } catch (error) {
      console.error("Error al obtener el soporte:", error);
      toast.error("No se pudo cargar el archivo de soporte", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  // Filtrar datos según los criterios de búsqueda
  const filteredData = novedades.filter((item) => {
    let matchesSearch = true;
    let matchesDateRange = true;
    let matchesCampus = true;
    let matchesTipo = true;

    // Filtro de búsqueda de texto
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      matchesSearch =
        item.empleado.toLowerCase().includes(searchLower) ||
        item.cedula.toLowerCase().includes(searchLower) ||
        item.cargo.toLowerCase().includes(searchLower) ||
        item.observaciones.toLowerCase().includes(searchLower);
    }

    // Filtro de rango de fechas
    if (dateRange && dateRange[0] && dateRange[1]) {
      const fecha = moment(item.fecha, "DD/MM/YYYY");
      const startDate = moment(dateRange[0]);
      const endDate = moment(dateRange[1]);
      matchesDateRange = fecha.isBetween(startDate, endDate, "day", "[]");
    }

    // Filtro de campus
    if (campus) {
      matchesCampus = item.campus === campus;
    }

    // Filtro de tipo de novedad
    if (tipo) {
      matchesTipo = item.tipo_novedad === tipo;
    }

    return matchesSearch && matchesDateRange && matchesCampus && matchesTipo;
  });

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando novedades..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Novedades de Personal
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddNovedad}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Agregar Novedad
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={["Fecha inicio", "Fecha fin"]}
                onChange={handleDateChange}
                value={dateRange}
                locale={locale}
                className="w-64"
              />
            </div>
            <div>
              <Select
                placeholder="Filtrar por Campus"
                allowClear
                style={{ width: 170 }}
                onChange={(value) => setCampus(value)}
                value={campus}
              >
                {campusOptions.map((campus) => (
                  <Option key={campus} value={campus}>
                    {campus}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Select
                placeholder="Filtrar por Tipo"
                allowClear
                style={{ width: 200 }}
                onChange={(value) => setTipo(value)}
                value={tipo}
              >
                {tiposNovedades.map((tipo) => (
                  <Option key={tipo.id} value={tipo.novedad}>
                    {tipo.novedad}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <Input
                placeholder="Buscar..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={handleSearch}
                className="w-60"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reiniciar filtros
            </Button>
            <Tooltip title="Exportar a Excel">
              <Button className="flex items-center bg-green-50 hover:bg-green-100 border-green-500 text-green-700">
                <img
                  src={icono_excel}
                  alt="Excel"
                  className="h-6 mr-1 object-cover"
                />
                <span>Exportar</span>
              </Button>
            </Tooltip>
          </div>
        </div>        <Table
          columns={columns}
          dataSource={filteredData}
          rowClassName="hover:bg-blue-50 transition-colors cursor-pointer"
          scroll={{ x: 1300, y: "calc(100vh - 350px)" }}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} novedades`,
          }}
          className="border border-gray-200 rounded-md"        />        {/* Modal para crear/editar novedades */}
        <FormNovedad
          visible={formVisible}
          onCancel={() => {
            setFormVisible(false);
            setEditingNovedad(null);
          }}          onSuccess={() => {
            console.log("[TablaNovedades] Ejecutando callback onSuccess...");
            try {
              setFormVisible(false);
              setEditingNovedad(null);
              console.log("[TablaNovedades] Modal cerrado y novedad limpiada");
              
              // Recargar las novedades de forma asíncrona
              fetchNovedades().catch(error => {
                console.error("[TablaNovedades] Error al recargar novedades:", error);
                // No mostrar toast de error aquí para no confundir al usuario
                // ya que la novedad se creó exitosamente
              });
              console.log("[TablaNovedades] Recarga de novedades iniciada");
            } catch (error) {
              console.error("[TablaNovedades] Error en callback onSuccess:", error);
            }
          }}
          novedadEditar={editingNovedad}
        />

        {/* Modal de confirmación para eliminar novedad */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Novedad"
          itemDescription="la novedad de"
          itemName={novedadToDelete ? `${novedadToDelete.empleado} del ${novedadToDelete.fecha}` : ""}
        />
      </Card>
    </div>
  );
};

export default TablaNovedades;
